import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import RoyalMailLabelModal from "@/components/RoyalMailLabelModal";
import { Link, useParams, useLocation, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useKidAuth } from "@/context/KidAuthContext";
import { formatGBP, courierFromPackageSize, conditionLabel, categoryLabel } from "@/lib/marketplace";
import { ArrowLeft, Truck, MapPin, CheckCircle2, RotateCcw, Loader2, Star, Globe, MessageCircle, Copy, Check, Printer, PackageCheck, Package, CircleCheck, Phone, Unlock, Send, Users, School, Mail, ExternalLink, Handshake, Zap, Leaf } from "lucide-react";

const ORDER_STATUS_LABELS = {
  pending: "Pending Payment",
  awaiting_payment: "Awaiting Payment",
  paid: "Paid — Awaiting Dispatch",
  processing: "Processing",
  escrow: "Escrow held",
  escrow_held: "Escrow held",
  awaiting_shipment: "Awaiting Dispatch",
  awaiting_collection: "Awaiting Collection",
  shipped: "Dispatched",
  collected: "Collected",
  delivered: "Delivered",
  released: "Completed",
  completed: "Completed",
  cancelled: "Cancelled",
  refunded: "Refunded",
};

const ORDER_STATUS_BADGE_CLASSES = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  awaiting_payment: "bg-amber-100 text-amber-800 border-amber-200",
  paid: "bg-blue-100 text-blue-800 border-blue-200",
  processing: "bg-blue-100 text-blue-800 border-blue-200",
  escrow: "bg-amber-100 text-amber-800 border-amber-200",
  escrow_held: "bg-amber-100 text-amber-800 border-amber-200",
  awaiting_shipment: "bg-blue-100 text-blue-800 border-blue-200",
  awaiting_collection: "bg-blue-100 text-blue-800 border-blue-200",
  shipped: "bg-sky-100 text-sky-800 border-sky-200",
  collected: "bg-emerald-100 text-emerald-800 border-emerald-200",
  delivered: "bg-emerald-100 text-emerald-800 border-emerald-200",
  released: "bg-emerald-100 text-emerald-800 border-emerald-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelled: "bg-rose-100 text-rose-800 border-rose-200",
  refunded: "bg-rose-100 text-rose-800 border-rose-200",
};

const formatOrderStatus = (status) => {
  const key = String(status || "").toLowerCase();
  if (ORDER_STATUS_LABELS[key]) return ORDER_STATUS_LABELS[key];
  const fallback = String(status || "Unknown").replace(/_/g, " ");
  return fallback.charAt(0).toUpperCase() + fallback.slice(1);
};

const orderStatusBadgeClass = (status) =>
  ORDER_STATUS_BADGE_CLASSES[String(status || "").toLowerCase()] || "bg-slate-100 text-slate-700 border-slate-200";


const formatSellerHandle = (value) => {
  const handle = String(value || "").trim().replace(/^@/, "");
  return handle ? `@${handle}` : "";
};

const ORDER_CONDITION_LABELS = {
  brand_new: "Brand New",
  like_new: "Almost New",
  almost_new: "Almost New",
  used_good: "Used - Good Condition",
  played_with: "Used - Good Condition",
  good: "Used - Good Condition",
  well_loved: "Well Loved",
};

function ItemSummaryCard({ order }) {
  const listingId = order.listing_id || order.toy_id || "";
  const title = order.item_title || order.listing_title || order.title || "Marketplace item";
  const description = order.description || order.item_description || order.listing_description || "No description was added to this listing.";
  const imageUrl = order.image_url || order.item_photo || order.listing_thumbnail || (order.photos || [])[0] || "";
  const condition = ORDER_CONDITION_LABELS[order.condition] || conditionLabel(order.condition);
  const category = categoryLabel(order.category || (order.categories || [])[0]);

  return (
    <section className="card-base p-5 mt-6" data-testid="order-item-summary">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-sm font-bold text-slate-700 inline-flex items-center gap-1.5">
          <Package className="w-4 h-4 text-brandTeal" /> Item Summary
        </h2>
        {order?.sponsored_purchase && (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-200" data-testid="order-sponsored-badge">
            🌟 Bought by Community Sponsorship Purchase Fund
          </span>
        )}
        {listingId && (
          <Link
            to={`/listing/${listingId}`}
            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-brandTeal/10 text-brandTeal hover:bg-brandTeal hover:text-white transition-colors"
            data-testid="order-view-original-listing"
          >
            View Original Listing <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
      {/* Iteration 292 — landfill impact on the order receipt. */}
      {(order?.weight_saved_kg || 0) > 0 && (
        <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-2" data-testid="order-impact-saved">
          <Leaf className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <span className="text-sm font-semibold text-emerald-800">
            You saved {Number(order.weight_saved_kg).toFixed(1)} kg of landfill waste!
          </span>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-[132px_1fr]">
        <div className="h-32 w-full sm:w-32 overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 flex items-center justify-center" data-testid="order-item-image-wrap">
          {imageUrl ? (
            <img src={imageUrl} alt={title} className="h-full w-full object-cover" loading="lazy" data-testid="order-item-image" />
          ) : (
            <div className="flex flex-col items-center gap-2 text-slate-400" data-testid="order-item-image-fallback">
              <Package className="w-8 h-8" />
              <span className="text-[10px] font-bold uppercase tracking-wider">No photo</span>
            </div>
          )}
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-800" data-testid="order-item-condition">
              {condition}
            </span>
            <span className="inline-flex rounded-full bg-sky-100 px-2.5 py-1 text-[11px] font-bold text-sky-800" data-testid="order-item-category">
              {category}
            </span>
          </div>
          <h3 className="font-display text-xl font-semibold text-brandSlate" data-testid="order-item-title">{title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-600 whitespace-pre-wrap" data-testid="order-item-description">{description}</p>
          <div className="mt-3 text-xs text-slate-500" data-testid="order-item-listing-id">
            <span className="font-bold uppercase tracking-wider text-slate-400">Listing ID / SKU:</span>{" "}
            <span className="font-mono break-all">{listingId || "—"}</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Iteration 175 — Unified Orders spec. Detail page always returns
  // to the master /orders list, preserving `?kid=<id>` when set so
  // the parent-kid context isn't lost during drill-down.
  const backTarget = React.useMemo(() => {
    const kidId = searchParams.get("kid") || "";
    return {
      to: kidId ? `/orders?kid=${encodeURIComponent(kidId)}` : "/orders",
      label: "All orders",
    };
  }, [searchParams]);
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [code, setCode] = useState("");
  const [rating, setRating] = useState({ star_count: 5, written_review: "" });
  const [ratingSent, setRatingSent] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [promptToast, setPromptToast] = useState("");
  // Iteration 115 — Copy Address button state (2s "Copied!" flash).
  const [addressCopied, setAddressCopied] = useState(false);
  // Iteration 118 — post-purchase reveal card + role-aware preset chips.
  const [reveal, setReveal] = useState(null);          // {seller_phone, collection_address, delivery_address, ...}
  const [postPurchasePrompts, setPostPurchasePrompts] = useState([]); // {key, title, template, role}
  const [postCopied, setPostCopied] = useState(false);
  // Iteration 145 — carrier + tracking shim payload (local override for
  // upstream orders that don't retain courier metadata).
  const [summary, setSummary] = useState(null);
  const { user } = useAuth();
  const { kid: kidSession } = useKidAuth();

  // Iteration 266 — post-purchase shipping details (kid Royal Mail
  // purchases complete address-free; the buyer's parent adds them here).
  const [sdName, setSdName] = useState("");
  const [sdLine1, setSdLine1] = useState("");
  const [sdLine2, setSdLine2] = useState("");
  const [sdCity, setSdCity] = useState("");
  const [sdPostcode, setSdPostcode] = useState("");
  const [sdBusy, setSdBusy] = useState(false);
  const [sdErr, setSdErr] = useState("");

  const sdValid = sdName.trim().length >= 2 && sdLine1.trim().length >= 2
    && sdCity.trim().length >= 1 && sdPostcode.trim().length >= 3;

  const saveShippingDetails = async () => {
    setSdBusy(true);
    setSdErr("");
    try {
      await api.post(`/orders/${id}/shipping-details`, {
        recipient_name: sdName.trim(),
        line1: sdLine1.trim(),
        line2: sdLine2.trim(),
        city: sdCity.trim(),
        postcode: sdPostcode.trim(),
      });
      toast.success("📮 Shipping details saved — the seller can now post the item.");
      await load();
    } catch (e) {
      setSdErr(formatApiError(e) || "Couldn't save — please try again.");
    } finally {
      setSdBusy(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      // Iteration 150 — surface the raw payload in the console so
      // parents / support can inspect exactly which keys the upstream
      // returned when a badge / delivery card / price fails to
      // render. Safe to leave on — it's a single log per load and
      // never contains bearer tokens.
      // eslint-disable-next-line no-console
      console.log("LIVE ORDER PAYLOAD:", data);
      setOrder(data);
    }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  useEffect(() => { api.get("/prompts/templates").then(({ data }) => setPrompts(data || [])).catch(() => setPrompts([])); }, []);
  // Iteration 145 — fetch carrier + tracking summary alongside the
  // core order fetch so the Delivery card can display shipping ref.
  useEffect(() => {
    if (!id) return;
    api.get(`/orders/${id}/summary`).then(({ data }) => setSummary(data || null)).catch(() => setSummary(null));
  }, [id]);

  // Iteration 118 — fetch the post-purchase preset library once (both
  // buyer + seller variants come back so we can slice by viewer role).
  useEffect(() => {
    api.get("/prompts/library?context=post_purchase")
      .then(({ data }) => setPostPurchasePrompts(Array.isArray(data) ? data : []))
      .catch(() => setPostPurchasePrompts([]));
  }, []);

  // Iteration 118 — auto-load the contact-reveal payload once the
  // order is confirmed paid. We treat any status that isn't
  // "pending" / "awaiting_payment" as paid (matches the upstream
  // PAID_ORDER_STATUSES set) and bail cleanly if upstream 403s (order
  // still processing).
  useEffect(() => {
    if (!order) return;
    const paidStatuses = ["paid", "processing", "escrow", "escrow_held", "awaiting_collection", "shipped", "collected", "delivered", "released", "completed"];
    if (!paidStatuses.includes((order.status || "").toLowerCase())) return;
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/orders/${id}/contact-reveal`);
        if (!cancel) setReveal(data);
      } catch { /* not yet unlocked — silent */ }
    })();
    return () => { cancel = true; };
  }, [order, id]);

  const sendPrompt = async (key) => {
    if (!order?.toy_id) return;
    setPromptToast("");
    try {
      await api.post("/prompts/send", { toy_id: order.toy_id, prompt_key: key, sender_kid_name: "Seller" });
      setPromptToast("Message sent to the buyer's parent.");
    } catch (e) { setPromptToast(formatApiError(e)); }
  };

  const [manualTracking, setManualTracking] = useState("");
  const [manualCarrier, setManualCarrier] = useState("Royal Mail");
  const manualDispatchReady = Boolean(manualTracking.trim() && manualCarrier.trim());
  const markShipped = async () => {
    const t = manualTracking.trim();
    const c = manualCarrier.trim();
    if (!c || !t) {
      setErr("Carrier name and tracking/reference number are required before marking this order as posted.");
      return;
    }
    setBusy("ship"); setErr("");
    try {
      await api.post(`/orders/${id}/mark-shipped`, { tracking_number: t, carrier: c });
      toast.success(`📮 Marked as posted · ${t}`);
      setManualTracking("");
      await load();
    }
    catch (e) { setErr(formatApiError(e)); } finally { setBusy(""); }
  };
  // Iteration 174 — Royal Mail Click & Drop label buy / re-print. On
  // success we open the returned PDF data URI in a new tab, refresh
  // the order (which now shows status=shipped + tracking) and cache
  // the label locally so the button can flip to "Re-print".
  const [labelInfo, setLabelInfo] = useState(null);
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/orders/${id}/shipping-label`);
        if (!cancelled) setLabelInfo(data);
      } catch { /* 404 is expected until the label is bought */ }
    })();
    return () => { cancelled = true; };
  }, [id]);  const printLabel = async () => {
    setBusy("label"); setErr("");
    try {
      let info = labelInfo;
      if (!info || !info.label_url) {
        const { data } = await api.post(`/orders/${id}/shipping-label`);
        info = data;
        setLabelInfo(data);
        toast.success("📮 Royal Mail label generated — pick QR or print below");
      }
      if (info?.label_url || info?.qr_code_url) {
        setLabelModalOpen(true);
      }
      await load();
    } catch (e) {
      // Iteration 235 — a busy/unreachable Royal Mail API surfaces as a
      // friendly toast, never an origin crash. The manual "Mark Item as
      // Posted / Add Tracking" card below stays fully functional.
      toast.error("Royal Mail service busy — please try again, or use manual tracking below.");
      setErr(formatApiError(e));
    } finally { setBusy(""); }
  };
  const confirmCollection = async () => {
    const pin = (code || "").trim();
    if (!pin) {
      setErr("Please enter the buyer's 4-digit PIN before confirming.");
      return;
    }
    setBusy("collect"); setErr("");
    try {
      await api.post(`/orders/${id}/verify-pin`, { pin });
      toast.success("🎉 Handover confirmed — funds released to your kid's wallet!");
      await load();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(""); }
  };
  const refund = async () => {
    if (!window.confirm("Issue a refund? This will reverse the payment.")) return;
    setBusy("refund"); setErr("");
    try { await api.post(`/orders/${id}/refund`, { reason: "Buyer request" }); await load(); }
    catch (e) { setErr(formatApiError(e)); } finally { setBusy(""); }
  };
  const submitRating = async (e) => {
    e.preventDefault();
    setBusy("rate"); setErr("");
    try {
      await api.post("/ratings", {
        reviewer_kid_id: order.buyer_kid_id || order.kid_id,
        reviewed_kid_id: order.kid_id,
        order_id: order.id,
        star_count: rating.star_count,
        written_review: rating.written_review,
      });
      setRatingSent(true);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(""); }
  };

  if (err && !order) return <div className="min-h-screen bg-[#FFFDF7]"><Navbar /><div className="p-20 text-center text-rose-600">{err}</div></div>;
  if (!order) return <div className="min-h-screen bg-[#FFFDF7]"><Navbar /><div className="p-20 text-center text-slate-500">Loading…</div></div>;

  // Iteration 253 — the ORIGINAL web checkout selection
  // (`fulfillment_method_web`) outranks upstream's mapped value, which
  // can collapse a Parent Handover into school_gate or even
  // shipping/home_delivery (the "Ship · Royal Mail" header bug).
  const fm = (order.fulfillment_method_web || order.fulfillment_method || "").toLowerCase();
  const isParentHandoff = fm === "parent_handoff";
  const isSchoolGate = fm === "school_gate" || fm === "collection";
  const isLocalShop = fm === "local_shop" || fm === "local_pickup";
  const isCollection = isParentHandoff || isSchoolGate || isLocalShop;
  const isPost = fm === "shipping" || fm === "home_delivery";
  // Iteration 253 — child-to-child purchases surface the linked parent
  // contact name (e.g. "Ruth Test") so parents can arrange
  // the handover smoothly.
  const isChildPurchase = Boolean(order.buyer_kid_id);
  const buyerKidTag = (order.buyer_kid_name && String(order.buyer_kid_name).toLowerCase() !== "guest") ? order.buyer_kid_name : null;
  // Iteration 256 — the Parent Name row must be the household PARENT
  // name, never the buyer kid's name: reject the buyer_name fallback
  // when it equals the kid tag.
  const buyerParentContact = order.buyer_parent_name
    || (order.buyer_name
        && String(order.buyer_name).toLowerCase() !== "guest"
        && String(order.buyer_name) !== String(buyerKidTag || "")
        ? order.buyer_name : null);
  // Iteration 255 — privacy gates:
  //  • Parent contact details (email/phone) only render once the order
  //    is paid/confirmed (`isPaidState`).
  //  • A pure Kid Zone session (no parent signed in) NEVER sees parent
  //    contact details — kids get a "Speak to Parent" prompt instead.
  //    While the kid-session probe is still running (kidSession ===
  //    null) we keep contacts locked as the safe default.
  const PAID_VISIBLE_STATUSES = ["paid", "processing", "escrow", "escrow_held", "awaiting_collection", "awaiting_shipment", "shipped", "collected", "completed", "delivered", "released"];
  const isPaidState = PAID_VISIBLE_STATUSES.includes((order.status || "").toLowerCase());
  // Iteration 273 — handover STATE LOCK: when the backend flags
  // `handover_pending_pin`, display + action gating use the locked
  // "awaiting_collection" status instead of upstream's premature
  // completed/delivered (kid-balance handovers auto-complete upstream
  // on approval, skipping the PIN step). Payment-gated surfaces keep
  // using the RAW status — the money genuinely moved.
  const displayStatus = (order.display_status || order.status || "").toLowerCase();
  const isKidViewer = !user && Boolean(kidSession);
  const contactsLocked = !user && kidSession !== false;
  // Iteration 189 — expand the handover PIN gate to cover every
  // "paid but not yet collected" upstream status. Upstream returns
  // `escrow_held` / `escrow` for parent-handoff orders while funds
  // sit in escrow, so the seller MUST be able to enter the PIN from
  // there — previously the UI showed "No actions available".
  const HANDOVER_READY_STATUSES = ["paid", "processing", "escrow", "escrow_held", "awaiting_collection"];
  // Iteration 234 — upstream flips paid orders to `escrow_held` (funds
  // sitting in escrow), NOT `paid`. Gating dispatch on "paid" alone
  // deadlocked every postal order in escrow (the "6 stuck orders").
  // Dispatch actions now unlock for the whole paid-but-not-shipped set.
  const DISPATCH_READY_STATUSES = ["paid", "processing", "escrow", "escrow_held", "awaiting_shipment"];
  const canShip = !isCollection && DISPATCH_READY_STATUSES.includes(order.status);
  // Iteration 273 — the seller's PIN entry follows the LOCKED display
  // status so auto-completed handovers reopen the Confirm Handover card.
  const canCollect = isCollection && HANDOVER_READY_STATUSES.includes(displayStatus);
  const canRefund = ["paid", "shipped"].includes(order.status);
  const isComplete = ["shipped", "collected"].includes(displayStatus);
  // Iteration 174 — the "Generate/Print label" affordance shows on any
  // Royal Mail Post order that is either still awaiting shipment
  // (paid/escrow) OR already shipped (so parents can re-print a lost
  // label). Once a label has been bought we swap copy to "Re-print".
  const canLabel = isPost && (DISPATCH_READY_STATUSES.includes(order.status) || (order.status === "shipped" && !!labelInfo?.label_url));
  const labelAlreadyBought = !!labelInfo?.label_url;
  const sellerHandleLabel = formatSellerHandle(
    order.seller_handle || order.seller_kid_handle || (order.seller && order.seller.handle)
  );
  // Iteration 270 — the Buyer badge shows the buyer kid's @handle
  // (e.g. "@kat-TV2") for child-to-child orders, mirroring the seller
  // badge; plain buyer_name ("Kat") is the fallback only.
  const buyerHandleLabel = formatSellerHandle(
    order.buyer_kid_handle || (order.buyer && order.buyer.handle)
  );

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Navbar />
      <section className="max-w-3xl mx-auto px-6 lg:px-8 py-10">
        <Link to={backTarget.to} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-blue-600" data-testid="order-back"><ArrowLeft className="w-4 h-4" /> Back to All Orders</Link>
        <div className="mt-4">
          <div className="pill bg-blue-100 text-blue-800">Order</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 mt-2" data-testid="order-title">{order.title || "Order"}</h1>
          {/* Iteration 145/148/150 — Fixed price header. Prefer the
              RAW item list price (`sale_price_pennies` /
              `item_price_pennies` — £3.00 for our example order)
              over the gross buyer-paid amount (`total_pennies` —
              £3.30). If the backend hasn't stamped a canonical list
              price yet, derive it on the client from `gross - fee`
              so we never render £0.00 for an order that clearly has
              cost data. */}
          {(() => {
            const feeP = Number(order.fee_pennies ?? order.platform_fee_pennies ?? 0);
            const grossP = Number(order.total_pennies ?? order.amount_pennies ?? order.gross_pennies ?? 0);
            const derivedListP = grossP > 0 && feeP > 0 ? grossP - feeP : 0;
            const pennies = Number(
              order.sale_price_pennies
              ?? order.item_price_pennies
              ?? (derivedListP > 0 ? derivedListP : null)
              ?? order.amount_pennies
              ?? order.total_pennies
              ?? order.gross_pennies
              ?? 0
            );
            const pounds = pennies > 0
              ? pennies / 100
              : Number(order.item_price ?? order.total_amount ?? order.amount ?? 0);
            return (
              <div className="text-2xl font-display text-blue-600 mt-2" data-testid="order-amount">
                {formatGBP(pounds)}
              </div>
            );
          })()}
          {/* Iteration 155 — Seller / Buyer identification badges.
              Bind STRICTLY to the order.* canonical fields (which
              now share the same household kid-name resolution as
              the Sales & Purchases list, so intra-household orders
              read "Seller: Kat" / "Buyer: Tom"). We NEVER read from
              `reveal.seller_name` here because that field carries
              the parent contact name (e.g. "Ruth Test"), not the
              child seller's alias. */}
          <div className="mt-3 flex flex-wrap gap-2" data-testid="order-parties">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-900 border border-emerald-200"
              data-testid="order-seller-badge"
            >
              Seller:{" "}
              <span data-testid="order-seller-handle">
                {sellerHandleLabel
                  || order.seller_name
                  || order.seller_kid_name
                  || (order.seller && (order.seller.kid_name || order.seller.name))
                  || order.kid_name
                  || "—"}
              </span>
            </span>
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-sky-100 text-sky-900 border border-sky-200"
              data-testid="order-buyer-badge"
            >
              Buyer: {
                buyerHandleLabel
                || order.buyer_name
                || order.buyer_kid_name
                || (order.buyer && (order.buyer.kid_name || order.buyer.name))
                || order.recipient_name
                || summary?.buyer_name
                || "—"
              }
            </span>
          </div>
          <div className="text-sm text-slate-500 mt-3 flex flex-wrap items-center gap-2">
            <span>Status:</span>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-bold ${orderStatusBadgeClass(displayStatus)}`}
              data-testid="order-status-badge"
            >
              {formatOrderStatus(displayStatus)}
            </span>
            <span aria-hidden="true">·</span>
            {isParentHandoff
              ? (<span data-testid="order-fulfillment-label"><Users className="inline w-3 h-3" /> Parent Handover</span>)
              : isLocalShop
                ? (<span data-testid="order-fulfillment-label"><MapPin className="inline w-3 h-3" /> Local Collection</span>)
                : isSchoolGate
                  ? (<span data-testid="order-fulfillment-label"><School className="inline w-3 h-3" /> School-Gate Collection</span>)
                  : (<span data-testid="order-fulfillment-label"><Truck className="inline w-3 h-3" /> Ship · {courierFromPackageSize(order.package_size).emoji} {courierFromPackageSize(order.package_size).name}</span>)}
          </div>
          {order.auto_approved && (
            <div className="mt-3 inline-flex items-center gap-1.5 pill bg-teal-100 text-teal-800" data-testid="order-auto-approved-tag">
              <Zap className="w-3 h-3" /> Auto-Approved via Piggy Bank Rule{order.auto_approve_threshold_pennies ? ` (Limit: ${formatGBP((order.auto_approve_threshold_pennies || 0) / 100)})` : ""}
            </div>
          )}

          {/* Iteration 266 — kid Royal Mail orders: the buyer's parent
              adds recipient + postal address post-purchase; both sides
              then see the saved delivery address here. */}
          {order.shipping_details && (
            <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3" data-testid="order-delivery-address">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Delivery address</div>
              <div className="text-sm font-semibold text-slate-800" data-testid="order-delivery-recipient">{order.shipping_details.recipient_name}</div>
              <div className="text-sm text-slate-600" data-testid="order-delivery-lines">
                {[
                  order.shipping_details.delivery_address?.line1,
                  order.shipping_details.delivery_address?.line2,
                  order.shipping_details.delivery_address?.city,
                  order.shipping_details.delivery_address?.postcode,
                ].filter(Boolean).join(", ")}
              </div>
            </div>
          )}
          {order.shipping_details_needed && order.role !== "sold" && order.seller_parent_id !== user?.id && (
            <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-4" data-testid="shipping-details-needed-banner">
              <div className="text-sm font-bold text-amber-900 flex items-center gap-2">
                <Truck className="w-4 h-4" /> Shipping details needed
              </div>
              <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                This Royal Mail purchase completed without a delivery address. Add the recipient name and postal address so the seller can post the item.
              </p>
              <div className="grid gap-2 mt-3" data-testid="shipping-details-form">
                <input
                  value={sdName}
                  onChange={(e) => setSdName(e.target.value)}
                  placeholder="Recipient full name"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  data-testid="sd-recipient-name"
                />
                <input
                  value={sdLine1}
                  onChange={(e) => setSdLine1(e.target.value)}
                  placeholder="Address line 1"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  data-testid="sd-line1"
                />
                <input
                  value={sdLine2}
                  onChange={(e) => setSdLine2(e.target.value)}
                  placeholder="Address line 2 (optional)"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  data-testid="sd-line2"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    value={sdCity}
                    onChange={(e) => setSdCity(e.target.value)}
                    placeholder="Town / City"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                    data-testid="sd-city"
                  />
                  <input
                    value={sdPostcode}
                    onChange={(e) => setSdPostcode(e.target.value)}
                    placeholder="Postcode"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm uppercase"
                    data-testid="sd-postcode"
                  />
                </div>
                {sdErr && (
                  <div className="text-xs text-rose-700" data-testid="sd-error" role="alert">{sdErr}</div>
                )}
                <button
                  type="button"
                  onClick={saveShippingDetails}
                  disabled={sdBusy || !sdValid}
                  className="btn-primary inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
                  data-testid="sd-save-btn"
                >
                  {sdBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
                  Save shipping details
                </button>
              </div>
            </div>
          )}
          {order.origin && (
            <div className="mt-3 inline-flex items-center gap-1.5 pill bg-blue-100 text-blue-800" data-testid="order-origin">
              <Globe className="w-3 h-3" /> Order placed {order.origin}
            </div>
          )}
        </div>

        {err && <div className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="order-error">{err}</div>}

        {/* Iteration 188 — Buyer Contact Details card. Surfaces the
            buyer_parent_email + buyer_parent_mobile captured at
            checkout so the SELLER can reach out to arrange the
            handoff (Parent Handoff / School-Gate) or clarify
            shipping details (Royal Mail). Only rendered when at
            least one contact field is present. */}
        {/* Iteration 255 — privacy: pure Kid Zone sessions see a
            "Speak to Parent" prompt instead of any contact details;
            parent viewers only see contacts once payment is confirmed. */}
        {contactsLocked && (order.buyer_name || order.buyer_parent_email || order.buyer_parent_mobile || order.buyer_kid_id) ? (
          <div className="card-base p-5 mt-4" data-testid="order-kid-privacy-notice">
            <div className="text-xs font-bold uppercase tracking-widest text-brandTeal mb-1 inline-flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Contact details are for parents
            </div>
            <p className="text-sm text-slate-600">
              Speak to your parent — the other family&apos;s parent contact details are only shared between parents.
            </p>
          </div>
        ) : isPaidState && (order.buyer_name || order.buyer_parent_email || order.buyer_parent_mobile || order.buyer_kid_handle) && (
          <div className="card-base p-5 mt-4" data-testid="order-buyer-contact-card">
            <div className="text-xs font-bold uppercase tracking-widest text-brandTeal mb-2 inline-flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Buyer Contact Details
            </div>
            <p className="text-xs text-slate-500 mb-3">Reach out to arrange the {isCollection ? "handoff" : "delivery"} or clarify any details.</p>
            <dl className="text-sm space-y-2">
              {/* Iteration 255/258 — full 4-part hierarchy for child-to-child
                  handovers: Child Handle → Parent Name → Email → Phone. */}
              {isChildPurchase && (buyerKidTag || order.buyer_kid_handle) && (
                <div className="flex items-center gap-2">
                  <dt className="text-slate-500 inline-flex items-center gap-1.5 w-28"><Users className="w-3.5 h-3.5" /> Child Handle</dt>
                  <dd className="flex-1 font-semibold text-slate-800" data-testid="order-buyer-child">
                    {buyerKidTag || "—"}{order.buyer_kid_handle ? ` (@${order.buyer_kid_handle})` : ""}
                  </dd>
                </div>
              )}
              {/* Iteration 256/258 — the Parent Name row shows the household
                  PARENT name (never the kid's), no prefix/suffix clutter. */}
              {isChildPurchase && buyerParentContact && (
                <div className="flex items-center gap-2">
                  <dt className="text-slate-500 inline-flex items-center gap-1.5 w-28"><Users className="w-3.5 h-3.5" /> Parent Name</dt>
                  <dd className="flex-1 font-semibold text-slate-800" data-testid="order-buyer-parent-contact">
                    {buyerParentContact}
                  </dd>
                </div>
              )}
              {!isChildPurchase && order.buyer_name && order.buyer_name.toLowerCase() !== "guest" && (
                <div className="flex items-center gap-2">
                  <dt className="text-slate-500 inline-flex items-center gap-1.5 w-20"><Users className="w-3.5 h-3.5" /> Name</dt>
                  <dd className="flex-1 font-semibold text-slate-800" data-testid="order-buyer-name">
                    {order.buyer_name}
                  </dd>
                </div>
              )}
              {order.buyer_parent_email && (
                <div className="flex items-center gap-2">
                  <dt className="text-slate-500 inline-flex items-center gap-1.5 w-28"><Mail className="w-3.5 h-3.5" /> {isChildPurchase ? "Parent Email" : "Email"}</dt>
                  <dd className="flex-1">
                    <a
                      href={`mailto:${order.buyer_parent_email}`}
                      className="text-brandTeal font-semibold hover:underline break-all"
                      data-testid="order-buyer-email"
                    >
                      {order.buyer_parent_email}
                    </a>
                  </dd>
                </div>
              )}
              {order.buyer_parent_mobile && (
                <div className="flex items-center gap-2">
                  <dt className="text-slate-500 inline-flex items-center gap-1.5 w-28"><Phone className="w-3.5 h-3.5" /> {isChildPurchase ? "Parent Phone" : "Mobile"}</dt>
                  <dd className="flex-1">
                    <a
                      href={`tel:${order.buyer_parent_mobile}`}
                      className="text-brandTeal font-semibold hover:underline"
                      data-testid="order-buyer-mobile"
                    >
                      {order.buyer_parent_mobile}
                    </a>
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {/* Iteration 273 — the buyer's 4-digit Handover PIN. Rendered
            ONLY on the buyer parent's view (the backend gates the
            field by caller identity) while the order awaits collection.
            The seller's "enter PIN" card is hidden on this view. */}
        {order.handover_pin && order.handover_pending_pin && (
          <div className="card-base p-5 mt-4 border-2 border-emerald-300 bg-emerald-50" data-testid="handover-pin-card">
            <div className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-1 inline-flex items-center gap-1.5">
              <Handshake className="w-3.5 h-3.5" /> Your Handover PIN
            </div>
            <div className="font-mono text-4xl font-bold tracking-[0.4em] text-emerald-900 my-2" data-testid="handover-pin-digits">
              {order.handover_pin}
            </div>
            <p className="text-xs text-emerald-800 leading-relaxed">
              Show this PIN to the seller&apos;s parent when you meet — they enter it to confirm the handover. Don&apos;t share it before the item is in your hands.
            </p>
          </div>
        )}

        <ItemSummaryCard order={order} />

        {/* Iteration 195 — Full itemised buyer receipt.
              Guest external checkout:  Item + Platform Fee + Postage + Stripe Fee
              Child-to-child internal :  Item + Platform Fee + Postage (no Stripe)
              Stripe row auto-hides for internal balance purchases
              (detected via presence of `buyer_kid_id`). Postage row
              appears whenever the order carries a Royal Mail postage
              charge (£3.50 flat). */}
        {(() => {
          const grossPennies = Number(order.amount_pennies || order.total_pennies || order.gross_pennies || 0);
          const postagePennies = Number(order.postage_pennies || 0);
          const stripeFeePennies = Number(order.stripe_fee_pennies || 0);
          // Iteration 223 — the Platform Fee row must show ONLY the 10%
          // platform cut. Prefer the explicit upstream/shim field; for
          // legacy rows that only carry a bundled `fee_pennies`
          // (gross − net = postage + platform + stripe lumped together),
          // unbundle by subtracting the known postage/stripe lines.
          const bundledFeePennies = Number(order.fee_pennies || 0);
          const feePennies =
            Number(order.platform_fee_pennies || order.protection_fee_pennies || 0) ||
            Math.max(0, bundledFeePennies - postagePennies - stripeFeePennies);
          if (feePennies <= 0 && grossPennies <= 0) return null;
          const knownAddonsPennies = feePennies + postagePennies + stripeFeePennies;
          // Prefer the explicit item list price; fall back to backing
          // the add-ons out of the buyer-paid gross.
          const listPricePennies =
            Number(order.item_price_pennies || order.sale_price_pennies || 0) ||
            Math.max(0, grossPennies - knownAddonsPennies);
          const buyerTotalPennies = grossPennies || (listPricePennies + knownAddonsPennies);
          const isInternal = Boolean(order.buyer_kid_id);
          return (
            <div className="card-base p-5 mt-6" data-testid="order-fee-receipt">
              <div className="text-sm font-bold text-slate-700 mb-3">Payment breakdown</div>
              <dl className="text-sm space-y-1.5">
                <div className="flex items-center justify-between">
                  <dt className="text-slate-600">Item list price</dt>
                  <dd className="font-semibold text-slate-900" data-testid="order-fee-item-price">
                    {formatGBP(listPricePennies / 100)}
                  </dd>
                </div>
                {feePennies > 0 && (
                  <div className="flex items-center justify-between">
                    <dt className="text-slate-600">Platform Fee (10%)</dt>
                    <dd className="font-semibold text-slate-900" data-testid="order-fee-safety-fee">
                      {formatGBP(feePennies / 100)}
                    </dd>
                  </div>
                )}
                {postagePennies > 0 && (
                  <div className="flex items-center justify-between">
                    <dt className="text-slate-600">Postage (Royal Mail)</dt>
                    <dd className="font-semibold text-slate-900" data-testid="order-fee-postage">
                      {formatGBP(postagePennies / 100)}
                    </dd>
                  </div>
                )}
                {!isInternal && stripeFeePennies > 0 && (
                  <div className="flex items-center justify-between">
                    <dt className="text-slate-600">Stripe processing (1.5% + 20p)</dt>
                    <dd className="font-semibold text-slate-900" data-testid="order-fee-stripe">
                      {formatGBP(stripeFeePennies / 100)}
                    </dd>
                  </div>
                )}
                <div className="border-t border-slate-100 pt-2 mt-2 flex items-center justify-between">
                  <dt className="text-brandSlate font-bold">Buyer paid</dt>
                  <dd className="text-lg font-display font-semibold text-brandSlate" data-testid="order-fee-buyer-total">
                    {formatGBP(buyerTotalPennies / 100)}
                  </dd>
                </div>
                {order.role === "sold" && (
                  <div className="flex items-center justify-between text-xs text-emerald-800 mt-1">
                    <dt>Seller net payout</dt>
                    <dd className="font-bold" data-testid="order-fee-seller-net">
                      {formatGBP(listPricePennies / 100)} · 100% of list price
                    </dd>
                  </div>
                )}
              </dl>
              <p className="text-[11px] text-slate-500 mt-3" data-testid="order-fee-note">
                Sellers keep 100% of the item list price. Platform Fee{postagePennies > 0 ? " and Postage route" : " routes"} to the company ledger{postagePennies > 0 ? " — postage funds the Royal Mail Click &amp; Drop label directly" : ""}. {isInternal ? "No Stripe fee on internal balance purchases." : ""}
              </p>
            </div>
          );
        })()}

        {/* Shipping address — Iteration 115 / 148 / 150: printable
            shipping-label layout (recipient → line 1 → line 2 →
            city → postcode → country) + one-tap Copy Address button.
            Iteration 150 broadens the render-gate to force the card
            whenever ANY delivery / shipping data exists (address OR
            standalone recipient name OR carrier OR tracking) so
            parents always see the block on shipping orders even
            when the address is partial. */}
        {(order.delivery_address || order.recipient_name || summary?.carrier || summary?.tracking_number) && (() => {
          const addr = order.delivery_address || {};
          const recipient = order.recipient_name || addr.recipient_name || "";
          // Prefer the granular `line1` / `line2` fields (iteration 115
          // backend now preserves them) and fall back to the legacy
          // combined `street` field so orders from earlier deploys still
          // render cleanly.
          const line1 = addr.line1 || addr.street || "";
          const line2 = addr.line2 || "";
          const city = addr.city || "";
          const postcode = addr.postcode || "";
          const country = addr.country || "GB";
          const labelLines = [
            recipient,
            line1,
            line2,
            [city, postcode].filter(Boolean).join(", "),
            country && country.toUpperCase() !== "GB" ? country : "United Kingdom",
          ].filter((s) => s && s.trim().length > 0);
          const copyAddress = async () => {
            try {
              await navigator.clipboard.writeText(labelLines.join("\n"));
              setAddressCopied(true);
              setTimeout(() => setAddressCopied(false), 2000);
            } catch { /* clipboard silently unavailable — leave state untouched */ }
          };
          return (
            <div className="card-base p-5 mt-6" data-testid="order-shipping">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="text-sm font-bold text-slate-700 inline-flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-blue-500" /> Delivery details
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => window.open(`/orders/${id}/label`, "_blank", "noopener")}
                    className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border bg-blue-50 border-blue-300 text-blue-800 hover:bg-blue-100 hover:border-blue-500 transition"
                    data-testid="order-open-print-label"
                    aria-label="Open printable shipping label in a new window"
                  >
                    <Printer className="w-3.5 h-3.5" /> Print label
                  </button>
                  <button
                    type="button"
                    onClick={copyAddress}
                    className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border transition ${addressCopied ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-white border-slate-300 hover:border-blue-500 hover:text-blue-600 text-slate-700"}`}
                    data-testid="order-copy-address"
                    aria-label="Copy shipping address to clipboard"
                  >
                    {addressCopied ? (
                      <>
                        <Check className="w-3.5 h-3.5" /> Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" /> Copy address
                      </>
                    )}
                  </button>
                </div>
              </div>
              {/* Printable shipping-label block — mono spaced font for
                  easy visual scan against a physical parcel label. */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 font-mono text-sm text-slate-900 leading-relaxed" data-testid="order-shipping-label">
                {recipient ? (
                  <div className="font-display text-base font-semibold text-slate-900 mb-1 not-italic" data-testid="order-recipient-name">
                    {recipient}
                  </div>
                ) : null}
                {line1 && <div data-testid="order-address-line1">{line1}</div>}
                {line2 && <div data-testid="order-address-line2">{line2}</div>}
                <div data-testid="order-address-city">
                  {city}{postcode ? `, ${postcode}` : ""}
                </div>
                <div className="text-slate-500 text-xs uppercase tracking-wider mt-1" data-testid="order-address-country">
                  {country && country.toUpperCase() !== "GB" ? country : "United Kingdom"}
                </div>
              </div>
              {!recipient && (
                <div className="mt-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="order-recipient-missing">
                  Recipient name not captured on this order — please confirm before shipping.
                </div>
              )}

              {/* Iteration 198 — Email + Mobile inline on the delivery
                  card so Royal Mail sellers can reach the buyer for
                  delivery clarifications without hunting through a
                  separate contact block. */}
              {(order.buyer_parent_email || order.buyer_parent_mobile) && (
                <div className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="order-buyer-inline-contact">
                  {order.buyer_parent_email && (
                    <div className="bg-white border border-slate-200 rounded-xl px-3 py-2">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Buyer email</div>
                      <a
                        href={`mailto:${order.buyer_parent_email}`}
                        className="text-sm font-semibold text-brandTeal hover:underline break-all"
                        data-testid="order-buyer-email-inline"
                      >
                        {order.buyer_parent_email}
                      </a>
                    </div>
                  )}
                  {order.buyer_parent_mobile && (
                    <div className="bg-white border border-slate-200 rounded-xl px-3 py-2">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Buyer mobile</div>
                      <a
                        href={`tel:${order.buyer_parent_mobile}`}
                        className="text-sm font-semibold text-brandTeal hover:underline"
                        data-testid="order-buyer-mobile-inline"
                      >
                        {order.buyer_parent_mobile}
                      </a>
                    </div>
                  )}
                </div>
              )}
              {/* Iteration 145 — Carrier + tracking (from local shim).
                  Populated once the seller-parent taps "Mark as
                  shipped" from the Ledger Order Summary drawer. */}
              {(summary?.carrier || summary?.tracking_number) && (
                <div className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="order-shipping-tracking">
                  <div className="bg-white border border-slate-200 rounded-xl px-3 py-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Carrier</div>
                    <div className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5" data-testid="order-shipping-carrier">
                      <Truck className="w-3.5 h-3.5 text-blue-500" />
                      {summary.carrier === "royal_mail" ? "Royal Mail" : summary.carrier === "evri" ? "Evri" : summary.carrier || "—"}
                    </div>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-xl px-3 py-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Tracking reference</div>
                    <div className="text-sm font-mono text-slate-900 break-all" data-testid="order-shipping-tracking-number">
                      {summary.tracking_number || <span className="text-slate-400 italic">not provided</span>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Iteration 116 — Order timeline step-tracker.
            Paid → Shipped → Collected/Delivered. Renders even for
            collection/school-gate orders (with "Collected" as the
            terminal state). Timestamps come straight from the order
            payload (created_at, shipped_at, collected_at). */}
        {(() => {
          const paidAt = order.paid_at || order.created_at;
          const shippedAt = order.shipped_at || summary?.shipped_at;
          // Iteration 273 — while the handover PIN is unconfirmed the
          // "Handed Over" pip stays unlit, even if upstream stamped
          // completed_at (order_out maps that into collected_at).
          const lockPin = Boolean(order.handover_pending_pin);
          const collectedAt = lockPin ? null : (order.collected_at || order.delivered_at);
          const status = (order.display_status || order.status || "").toLowerCase();
          // Iteration 145 — `completed` acts as a terminal "everything
          // done" status: all three timeline pips light green.
          // Iteration 273 — the handover state lock: locked orders
          // render from display_status ("awaiting_collection") so the
          // "Handed Over" pip stays unlit until PIN confirmation.
          const COMPLETED_STATUSES = ["completed", "delivered", "released"];
          const isPaid = Boolean(paidAt) || displayStatus !== "pending";
          // Refunded orders short-circuit the tracker.
          if (displayStatus === "refunded" || displayStatus === "cancelled") {
            return (
              <div className="card-base p-5 mt-6" data-testid="order-timeline">
                <div className="text-sm font-bold text-slate-700 inline-flex items-center gap-1.5 mb-2">
                  <Package className="w-4 h-4 text-slate-500" /> Order status
                </div>
                <div className="text-sm text-rose-700 font-semibold" data-testid="order-timeline-refunded">
                  This order was {status}. No further steps.
                </div>
              </div>
            );
          }
          const isShipped = Boolean(shippedAt) || ["shipped", "collected", ...COMPLETED_STATUSES].includes(status);
          const isCollected = Boolean(collectedAt) || ["collected", ...COMPLETED_STATUSES].includes(status);
          const finalLabel = isCollection ? "Handed Over" : "Delivered";
          const paidStep = {
            key: "paid",
            label: "Paid",
            doneLabel: "Paid",
            helper: "Payment received",
            done: isPaid,
            active: isPaid && !isCollected && !isShipped,
            at: paidAt,
            icon: CircleCheck,
          };
          const shippedStep = {
            key: "shipped",
            label: "Shipped",
            doneLabel: "Dispatched",
            helper: "Dispatched by seller",
            done: isShipped,
            active: isShipped && !isCollected,
            at: shippedAt,
            icon: Truck,
          };
          // Iteration 253 — in-person handovers get their own middle
          // step (funds secured → arrange the meetup) and NEVER show
          // "Dispatched by seller".
          const handoverStep = {
            key: "handover",
            label: "Ready for Handover",
            doneLabel: "Ready for handover",
            helper: "Arrange a time to meet",
            done: isPaid,
            active: isPaid && !isCollected,
            at: paidAt,
            icon: Handshake,
          };
          const collectedStep = {
            key: "collected",
            label: finalLabel,
            doneLabel: isCollection ? "Handed over" : "Delivered",
            helper: isCollection ? "Confirm at handover with the PIN" : "Confirmed by buyer",
            done: isCollected,
            active: isCollected,
            at: collectedAt,
            icon: PackageCheck,
          };
          // Iteration 253 — handover timeline reads
          // Paid → Ready for Handover → Handed Over.
          const steps = isCollection ? [paidStep, handoverStep, collectedStep] : [paidStep, shippedStep, collectedStep];
          const fmtDate = (iso) => {
            if (!iso) return "";
            try {
              return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
            } catch { return String(iso).slice(0, 16); }
          };
          return (
            <div className="card-base p-5 mt-6" data-testid="order-timeline">
              <div className="text-sm font-bold text-slate-700 inline-flex items-center gap-1.5 mb-4">
                <Package className="w-4 h-4 text-blue-500" /> Order timeline
              </div>
              <ol className="relative flex flex-col md:flex-row md:items-start gap-4 md:gap-0" data-testid="order-timeline-steps">
                {steps.map((s, idx) => {
                  const Icon = s.icon;
                  const nextDone = idx < steps.length - 1 ? steps[idx + 1].done : true;
                  const barCls = s.done && nextDone ? "bg-emerald-400" : (s.done ? "bg-emerald-200" : "bg-slate-200");
                  const badgeCls = s.done
                    ? "bg-emerald-500 text-white border-emerald-500"
                    : s.active
                      ? "bg-white text-blue-600 border-blue-500 ring-4 ring-blue-100"
                      : "bg-white text-slate-400 border-slate-300";
                  return (
                    <li key={s.key} className="relative flex md:flex-col md:items-center md:flex-1 gap-3 md:gap-0" data-testid={`order-timeline-step-${s.key}`}>
                      <div className="flex md:flex-col items-center gap-2 md:gap-0">
                        <div className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${badgeCls}`}>
                          {s.done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
                        </div>
                        {idx < steps.length - 1 && (
                          <div className={`hidden md:block absolute top-4 left-1/2 h-1 rounded ${barCls}`} style={{ width: "100%", zIndex: -1 }} aria-hidden="true" />
                        )}
                      </div>
                      <div className="md:mt-2 md:text-center">
                        <div className={`text-sm font-bold ${s.done ? "text-emerald-800" : s.active ? "text-blue-700" : "text-slate-500"}`}>
                          {s.label}
                        </div>
                        <div className="text-[11px] text-slate-500 md:mt-0.5">
                          {s.at ? (
                            <span data-testid={`order-timeline-time-${s.key}`}>
                              {s.doneLabel || s.label} on {fmtDate(s.at)}
                            </span>
                          ) : (
                            s.helper
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          );
        })()}

        {/* Lifecycle actions */}
        <div className="card-base p-6 mt-6" data-testid="order-actions">
          <h2 className="font-display text-xl font-medium text-slate-900 mb-3">Next steps</h2>
          {canShip && (
            <div className="card-base border-brandTeal/30 p-5 mt-4" data-testid="order-manual-dispatch-card">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-brandTeal mb-1">Manual dispatch</div>
                  <div className="text-sm font-bold text-slate-800">Mark Item as Posted / Add Tracking</div>
                </div>
                <span className="inline-flex items-center rounded-full bg-brandTeal/10 px-3 py-1 text-[11px] font-bold text-brandTeal" data-testid="order-manual-primary-badge">
                  Primary method
                </span>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Purchase postage at the Post Office counter or online, then enter the carrier and tracking/reference number below.
              </p>
              <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] items-end">
                <div>
                  <label htmlFor="order-manual-carrier" className="text-xs font-semibold text-slate-700">Carrier Name <span className="text-rose-500">*</span></label>
                  <input
                    id="order-manual-carrier"
                    type="text"
                    value={manualCarrier}
                    onChange={(e) => setManualCarrier(e.target.value)}
                    placeholder="Royal Mail"
                    required
                    aria-required="true"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-brandTeal focus:ring-2 focus:ring-brandTeal/30 outline-none"
                    data-testid="order-manual-carrier-input"
                  />
                </div>
                <div>
                  <label htmlFor="order-manual-tracking" className="text-xs font-semibold text-slate-700">Tracking/Reference Number <span className="text-rose-500">*</span></label>
                  <input
                    id="order-manual-tracking"
                    type="text"
                    value={manualTracking}
                    onChange={(e) => setManualTracking(e.target.value.toUpperCase())}
                    placeholder="e.g. AB123456789GB"
                    required
                    aria-required="true"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-mono focus:border-brandTeal focus:ring-2 focus:ring-brandTeal/30 outline-none"
                    data-testid="order-manual-tracking-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={markShipped}
                  disabled={busy === "ship" || !manualDispatchReady}
                  title={manualDispatchReady ? "Mark as posted" : "Enter carrier name and tracking/reference number first"}
                  className="btn-primary whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60"
                  data-testid="order-mark-shipped"
                >
                  {busy === "ship" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />} Mark as Posted
                </button>
              </div>
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-900" data-testid="order-manual-payout-notice">
                <strong>Note for Sellers:</strong> You will pay for postage upfront when dispatching manually. Entering a valid Tracking/Reference Number is required. Once you submit tracking details and click &apos;Mark as Posted&apos;, the £3.50 buyer postage payment will be released back to your Parent Wallet along with your item earnings!
              </div>
            </div>
          )}
          {canLabel && !labelAlreadyBought && (
            <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="order-auto-label-disabled">
              <button
                type="button"
                disabled
                aria-disabled="true"
                aria-describedby="order-auto-label-coming-soon"
                title="Automated Labels Coming Soon — Please Use Manual Dispatch"
                className="btn-primary opacity-50 cursor-not-allowed"
                data-testid="order-print-label"
              >
                <Printer className="w-4 h-4" /> Generate Royal Mail QR Code &amp; Label
              </button>
              <span
                id="order-auto-label-coming-soon"
                className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-bold text-amber-800"
                data-testid="order-auto-label-coming-soon"
              >
                Automated Labels Coming Soon — Please Use Manual Dispatch
              </span>
            </div>
          )}
          {labelAlreadyBought && (labelInfo?.tracking_number || labelInfo?.qr_code_url) && (
            <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900" data-testid="order-label-tracking">
              <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">Royal Mail tracking</div>
              {labelInfo.tracking_number && (
                <div className="font-mono text-sm mt-0.5" data-testid="order-label-tracking-number">{labelInfo.tracking_number}</div>
              )}
              {labelInfo.qr_code_url && (
                <div className="mt-3 text-center" data-testid="order-label-qr-inline">
                  <img
                    src={labelInfo.qr_code_url}
                    alt="Royal Mail QR code — scan at the Post Office"
                    className="w-44 h-44 mx-auto rounded-xl border border-emerald-200 bg-white p-2"
                    data-testid="order-label-qr-image"
                  />
                  <p className="text-[11px] text-emerald-800 mt-2">
                    No printer? Show this QR code at any Post Office — they&rsquo;ll print the label for you.
                  </p>
                </div>
              )}
            </div>
          )}
          {/* Iteration 273 — the PIN-entry card is the SELLER's tool;
              hidden on the buyer's view (which shows the PIN itself). */}
          {canCollect && !order.handover_pin && (
            <div className="card-base p-5 mt-4" data-testid="order-handoff-pin-card">
              <div className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-1">
                {isParentHandoff ? "Parent Handoff" : "School-Gate Collection"}
              </div>
              <div className="text-sm font-bold text-slate-800 mb-1">Confirm Handover</div>
              <p className="text-xs text-slate-500 mb-3">Ask the buyer&apos;s parent for their 4-digit PIN when you meet and enter it below to confirm collection and release funds.</p>
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label htmlFor="order-handoff-pin" className="sr-only">4-Digit Handover PIN</label>
                  <input
                    id="order-handoff-pin"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    className="input-base !py-2 !w-40 text-center font-mono text-lg tracking-[0.35em]"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="4291"
                    data-testid="order-collection-code"
                  />
                </div>
                <button onClick={confirmCollection} disabled={busy === "collect" || !code} className="btn-primary" data-testid="order-confirm-collection">
                  {busy === "collect" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Verify PIN &amp; Mark Collected
                </button>
              </div>
            </div>
          )}
          {canRefund && (
            <button onClick={refund} disabled={busy === "refund"} className="btn-secondary mt-3" data-testid="order-refund">
              {busy === "refund" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Issue refund
            </button>
          )}
          {!canShip && !canCollect && !canRefund && (
            <div className="text-sm text-slate-500" data-testid="order-no-actions">No actions available at this stage.</div>
          )}
        </div>

        {/* Rating */}
        {isComplete && !ratingSent && order.kid_id && (
          <form onSubmit={submitRating} className="card-base p-6 mt-6" data-testid="order-rating-form">
            <h2 className="font-display text-xl font-medium text-slate-900">Leave a rating</h2>
            <p className="text-sm text-slate-500 mb-3">How was the seller?</p>
            <div className="flex items-center gap-1 mb-3" data-testid="order-rating-stars">
              {[1,2,3,4,5].map((n) => (
                <button key={n} type="button" onClick={() => setRating({ ...rating, star_count: n })}
                  className="text-3xl">{n <= rating.star_count ? "⭐" : "☆"}</button>
              ))}
            </div>
            <textarea className="input-base min-h-[80px]" placeholder="A few kind words (optional)"
              value={rating.written_review} onChange={(e) => setRating({ ...rating, written_review: e.target.value })}
              data-testid="order-rating-text" />
            <button type="submit" disabled={busy === "rate"} className="btn-primary mt-3" data-testid="order-rating-submit">
              {busy === "rate" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />} Submit rating
            </button>
          </form>
        )}
        {ratingSent && <div className="card-base p-6 mt-6 text-center text-emerald-700 bg-emerald-50 border-emerald-200" data-testid="order-rating-thanks">Thanks for the rating!</div>}

        {/* Iteration 118 — Post-Purchase Fulfilment Card + role-aware
            preset chips. Renders below the timeline once the payment
            reveal endpoint returns seller phone / address.
            Iteration 255 — hidden entirely for pure Kid Zone sessions
            (contactsLocked) so kids never see parent contact details. */}
        {reveal && !contactsLocked && (() => {
          const sellerPhone = reveal.seller_phone || "";
          const sellerName = reveal.seller_name || order?.seller_kid_name || "the seller";
          const collectionAddress = reveal.collection_address;
          // Iteration 152 — prefer the delivery address stamped on the
          // main /orders/{id} payload when the /contact-reveal call
          // omitted it. Fall through: reveal → order → shipping_address
          // aliases → separate address_line1 style fields.
          const deliveryAddress = reveal.delivery_address
            || order?.delivery_address
            || order?.shipping_address
            || null;
          // Iteration 152 — treat the order as SHIP whenever ANY of the
          // upstream fulfillment aliases resolve to shipping /
          // delivery. This prevents the meetup fallback from firing
          // on postal orders where the reveal endpoint sent back a
          // partial payload.
          // Iteration 253 — the original web checkout selection wins
          // over upstream's mapped value; handover keywords now count
          // as collection (previously a parent_handoff order fell
          // through to the shipping branch).
          const _ff = ((
            order?.fulfillment_method_web
            || reveal.fulfillment_method
            || order?.fulfillment_method
            || order?.fulfillment_type
            || order?.shipping_method
            || ""
          ) + "").toLowerCase();
          const isCollectionOrder = _ff.includes("collect") || _ff.includes("school") || _ff.includes("handoff") || _ff.includes("handover");
          const isShippingOrder = _ff.includes("ship") || _ff.includes("deliver") || _ff.includes("post");
          const addressLines = (() => {
            const a = isCollectionOrder ? collectionAddress : deliveryAddress;
            if (!a) return [];
            if (typeof a === "string") return [a];
            return [
              a.recipient_name || a.name || reveal.recipient_name || order?.recipient_name || order?.buyer_name || "",
              a.line1 || a.street || order?.address_line1 || "",
              a.line2 || order?.address_line2 || "",
              [a.city, a.postcode || a.post_code].filter(Boolean).join(", "),
              a.country && a.country.toUpperCase() !== "GB" ? a.country : "United Kingdom",
            ].filter((s) => s && s.trim().length > 0);
          })();
          // Iteration 119 — treat phone + address as OPTIONAL. Missing
          // phone → hide the Call button and show an "in-app chat"
          // note. Missing exact street → show the seller's town + a
          // note nudging both parents to arrange the pickup spot via
          // the chat chips below.
          const hasStreet = (() => {
            const a = isCollectionOrder ? collectionAddress : deliveryAddress;
            if (!a || typeof a === "string") return typeof a === "string" && a.trim().length > 0;
            return Boolean((a.line1 || a.street || order?.address_line1 || "").trim());
          })();
          const sellerCity = (() => {
            const a = collectionAddress || {};
            if (typeof a === "string") return "";
            return (a.city || reveal.seller_city || "").trim();
          })();
          const copyReveal = async () => {
            try {
              await navigator.clipboard.writeText(addressLines.join("\n"));
              setPostCopied(true);
              setTimeout(() => setPostCopied(false), 2000);
            } catch { /* no clipboard — silent */ }
          };
          return (
            <div className="card-base p-6 mt-6 bg-emerald-50 border-emerald-200" data-testid="order-fulfilment-card">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                  <Unlock className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-display text-lg font-semibold text-emerald-900">Fulfilment details unlocked</div>
                  <div className="text-xs text-emerald-800">Contact details reveal after payment is confirmed.</div>
                </div>
                <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-500 text-white" data-testid="order-fulfilment-badge">
                  <CheckCircle2 className="w-3 h-3" /> Paid · #{(order.id || "").slice(0, 8).toUpperCase()}
                </span>
              </div>
              <div className={`grid gap-4 ${isShippingOrder ? "md:grid-cols-1" : "md:grid-cols-2"}`}>
                <div className="bg-white border border-emerald-100 rounded-2xl p-4" data-testid="fulfilment-seller-tile">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-800">Seller / Fulfilment contact</div>
                  {/* Iteration 258 — clean 4-row hierarchy: Child Handle →
                      Parent Name → Parent Email → Parent Phone. Handle
                      source is stripped of any "@" before prefixing
                      (fixes the "@@sg-ZTW" double-symbol bug). */}
                  <dl className="text-sm space-y-1.5 mt-1.5">
                    {(reveal.seller_kid_handle || sellerHandleLabel) && (
                      <div className="flex items-center gap-2">
                        <dt className="text-slate-500 w-28">Child Handle</dt>
                        <dd className="flex-1 font-semibold text-slate-800" data-testid="fulfilment-seller-handle">
                          @{String(reveal.seller_kid_handle || sellerHandleLabel || "").replace(/^@+/, "")}
                        </dd>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <dt className="text-slate-500 w-28">Parent Name</dt>
                      <dd className="flex-1 font-semibold text-slate-800" data-testid="fulfilment-seller-name">{sellerName}</dd>
                    </div>
                    {reveal.seller_email && (
                      <div className="flex items-center gap-2">
                        <dt className="text-slate-500 w-28">Parent Email</dt>
                        <dd className="flex-1">
                          <a
                            href={`mailto:${reveal.seller_email}`}
                            className="text-brandTeal font-semibold hover:underline break-all"
                            data-testid="fulfilment-seller-email"
                          >
                            {reveal.seller_email}
                          </a>
                        </dd>
                      </div>
                    )}
                    {sellerPhone && (
                      <div className="flex items-center gap-2">
                        <dt className="text-slate-500 w-28">Parent Phone</dt>
                        <dd className="flex-1 font-mono text-slate-800 tracking-wide" data-testid="fulfilment-seller-phone">{sellerPhone}</dd>
                      </div>
                    )}
                  </dl>
                  {sellerPhone ? (
                    <a
                      href={`tel:${sellerPhone}`}
                      className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold transition"
                      data-testid="fulfilment-call-seller"
                    >
                      <Phone className="w-3.5 h-3.5" /> Call seller
                    </a>
                  ) : (
                    <div
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-slate-100 border border-slate-200 rounded-full px-3 py-1.5"
                      data-testid="fulfilment-no-phone-note"
                    >
                      <MessageCircle className="w-3.5 h-3.5 text-slate-500" />
                      Contact via in-app chat
                    </div>
                  )}
                </div>
                {/* Iteration 157 — for postal shipping orders, hide
                    the entire address tile inside "Fulfilment details
                    unlocked". The top-level "Delivery details" card
                    (matching the working POSTAL ADDRESS design) is
                    the single source of truth — no duplicate block
                    and NEVER the pickup fallback. Collection /
                    school-gate orders keep this tile since they
                    genuinely need a meet-up spot. */}
                {!isShippingOrder && (
                <div className="bg-white border border-emerald-100 rounded-2xl p-4" data-testid="fulfilment-address-tile">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-800">
                      {isCollectionOrder ? "Collection address" : "Shipping address"}
                    </div>
                    {hasStreet && addressLines.length > 0 && (
                      <button
                        type="button"
                        onClick={copyReveal}
                        className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-full border transition ${postCopied ? "bg-emerald-100 border-emerald-300 text-emerald-900" : "bg-white border-slate-300 hover:border-blue-500 hover:text-blue-600 text-slate-700"}`}
                        data-testid="fulfilment-copy-address"
                      >
                        {postCopied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                      </button>
                    )}
                  </div>
                  {hasStreet && addressLines.length > 0 ? (
                    <div className="mt-2 font-mono text-sm text-slate-900 leading-relaxed" data-testid="fulfilment-address-block">
                      {addressLines.map((l, i) => <div key={i}>{l}</div>)}
                    </div>
                  ) : (
                    <div className="mt-2 space-y-1" data-testid="fulfilment-no-address-note">
                      {sellerCity ? (
                        <div className="text-sm font-semibold text-slate-900" data-testid="fulfilment-seller-city">
                          <MapPin className="inline w-3.5 h-3.5 text-slate-500 mr-1 -mt-0.5" />
                          {sellerCity}
                        </div>
                      ) : null}
                      <div className="text-xs text-slate-600 inline-flex items-center gap-1.5 mt-1">
                        <MessageCircle className="w-3.5 h-3.5 text-slate-500" />
                        Arrange exact pickup spot via in-app chat.
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Iteration 118 — Post-purchase preset chips (buyer OR seller,
            role-aware). Rendered under the fulfilment card once the
            reveal is unlocked. Clicking a chip immediately fires the
            prompt through /prompts/send. */}
        {reveal && postPurchasePrompts.length > 0 && (() => {
          // Detect the viewer role — the backend's `order.role` field
          // is authoritative (iteration 109): "sold" → seller-side,
          // "bought" → buyer-side. Fall back to comparing the parent
          // ids if role isn't stamped.
          let viewerRole = order?.role === "sold" ? "seller"
                          : order?.role === "bought" ? "buyer"
                          : null;
          if (!viewerRole && user?.id) {
            viewerRole = order?.seller_parent_id === user.id ? "seller" : "buyer";
          }
          if (!viewerRole) viewerRole = "buyer";
          const chips = postPurchasePrompts.filter((c) => c.role === viewerRole);
          if (chips.length === 0) return null;
          return (
            <div className="card-base p-6 mt-6" data-testid={`order-post-prompts-${viewerRole}`}>
              <h2 className="font-display text-xl font-medium text-slate-900 flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-500" /> Quick messages
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {viewerRole === "seller"
                  ? "Fire a friendly nudge to the buyer — tap to send."
                  : "Coordinate collection with the seller — tap to send."}
              </p>
              <div className="grid sm:grid-cols-2 gap-2 mt-4">
                {chips.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => sendPrompt(p.key)}
                    className="text-left p-3 border border-slate-200 rounded-xl hover:bg-slate-50 hover:border-blue-400 transition-all"
                    data-testid={`order-post-prompt-${p.key}`}
                  >
                    <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">{p.title}</div>
                    <div className="text-sm text-slate-800 mt-1">{p.template}</div>
                  </button>
                ))}
              </div>
              {promptToast && <div className="mt-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl p-2" data-testid="order-post-prompts-toast">{promptToast}</div>}
            </div>
          );
        })()}

        {/* Fixed prompt-only communication matrix (matches mobile spec) */}
        {prompts.length > 0 && (
          <div className="card-base p-6 mt-6" data-testid="order-prompts-matrix">
            <h2 className="font-display text-xl font-medium text-slate-900 flex items-center gap-2"><MessageCircle className="w-5 h-5 text-blue-500" /> Message the buyer</h2>
            <p className="text-sm text-slate-500 mt-1">Pick a quick safe message. No free text — protects both kids.</p>
            <div className="grid sm:grid-cols-2 gap-2 mt-4">
              {prompts.map((p) => (
                <button key={p.key} type="button" onClick={() => sendPrompt(p.key)}
                  className="text-left p-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all"
                  data-testid={`order-prompt-${p.key}`}>
                  <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">{p.title || p.key}</div>
                  <div className="text-sm text-slate-800 mt-1">{p.template || p.text}</div>
                </button>
              ))}
            </div>
            {promptToast && <div className="mt-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl p-2" data-testid="order-prompts-toast">{promptToast}</div>}
          </div>
        )}
      </section>
      <RoyalMailLabelModal
        open={labelModalOpen}
        onClose={() => setLabelModalOpen(false)}
        labelInfo={labelInfo}
        orderId={id}
      />
      <Footer />
    </div>
  );
}
